import React from 'react';
import { connect } from 'react-redux';

import { fetchValue, setValue, saveValue, setDeveloperName, setDuplicateWarning } from '../../../../../../actions/reduxActions/valueEditor';
import Loader from '../../../../../Loader';
import ValueEditor from './ValueEditor';
import ConfirmBody from '../../../../../generic/confirmBody';
import translations from '../../../../../../translations';
import { stringReplace } from '../../../../../../utils/core';

/**
 * Wrapper for the ValueEditor to manage fetching and saving a Value for creation or update.
 *
 * @param {String}   valueId      an optional Id for a Value. If provided, the Value is read and presented for editing, otherwise
 *                                a new empty Value is presented for creation.
 * @param {Object}   value        The complete Value object to be saved
 * @param {Array}    typeElements A list of all the available type definitions
 * @param {Function} switchView   Callback to return to the ValuePicker once editing is complete
 * @param {Function} select       Callback to set the edited Value
 * @param {Boolean}  isLoading    > 0 if currently fetching from API
 * @param {Boolean}  isSaving     True if currently saving to the API
 * @param {Boolean}  isDuplicateWarning True if the user has just tried to save a Value with a name that already exists
 * @param {String}   startingValueName  an optional string provided that starts every newly created value with that name
 */
export class ValueEditorContainer extends React.Component {

    componentDidMount() {
        const { authenticationToken, tenantId } = this.props.currentUser;
        if (this.props.valueId) {
            this.props.fetchValue(this.props.valueId, this.props.typeElements, authenticationToken, tenantId);
        }
        if (this.props.startingValueName) {
            this.props.setDeveloperName({ type: 'startingValue', value: this.props.startingValueName });
        }
    }

    componentWillUnmount() {
        this.props.setValue(null);
    }

    onSubmit = (e) => {
        const { authenticationToken, tenantId } = this.props.currentUser;

        e.preventDefault();
        this.props.saveValue(this.props.value, authenticationToken, tenantId, this.props.isDuplicateWarning)
            .then(response => response ?
                // Back to Value Selection
                this.props.select(response) :
                // Or an error occurred
                // Or there is already a Value with the chosen name and a warning will appear
                null);
    }

    render() {
        return (
            <div className="value-editor-container">
                {this.props.isLoading ? <Loader /> : 
                    this.props.isDuplicateWarning ? 
                        (
                            <ConfirmBody
                                title={
                                    this.props.value.id ? 'Edit Value' : 'Create new Value'
                                }
                                messages={[
                                    stringReplace(
                                        translations.VS_duplicate_value_1,
                                        this.props.value.developerName,
                                    ),
                                    translations.VS_duplicate_value_2,
                                ]}
                                onCancel={() => this.props.setDuplicateWarning(false)}
                                onConfirm={this.onSubmit}
                            />
                        ) :
                        (
                            <ValueEditor
                                onSubmit={this.onSubmit}
                                switchView={this.props.switchView}
                            />
                        )
                }
                {this.props.isSaving ? <Loader /> : null}
            </div>
        );
    }
}

const mapStateToProps = ({
    currentUser,
    typeElements,
    valueEditor: { value, isLoading, isSaving, isDuplicateWarning },
}) => ({ currentUser, typeElements: typeElements.types, value, isLoading, isSaving, isDuplicateWarning });

const mapDispatchToProps = {
    fetchValue,
    setValue,
    saveValue,
    setDeveloperName,
    setDuplicateWarning,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ValueEditorContainer);
